import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

type ReturnStatus = "pending" | "approved" | "rejected" | "completed";
type RefundMethod = "cash" | "transfer" | "store_credit" | null;

interface ReturnItem {
  id: number;
  order_id: string;
  customer_name: string;
  customer_phone: string | null;
  product_name: string;
  quantity: number;
  reason: string;
  status: ReturnStatus;
  admin_notes: string | null;
  refund_amount: number | null;
  refund_method: RefundMethod;
  created_at: string;
}

const STATUS_CONFIG: Record<ReturnStatus, { label: string; color: string; bg: string; border: string; icon: string }> = {
  pending:   { label: "قيد المراجعة", color: "text-amber-400",  bg: "bg-amber-400/10",  border: "border-amber-400/20",  icon: "ri-time-line" },
  approved:  { label: "مقبول",        color: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/20", icon: "ri-checkbox-circle-line" },
  rejected:  { label: "مرفوض",        color: "text-red-400",     bg: "bg-red-400/10",     border: "border-red-400/20",    icon: "ri-close-circle-line" },
  completed: { label: "مكتمل",        color: "text-teal-400",    bg: "bg-teal-400/10",    border: "border-teal-400/20",   icon: "ri-check-double-line" },
};

const REFUND_METHOD_LABELS: Record<string, string> = {
  cash: "نقداً",
  transfer: "تحويل بنكي",
  store_credit: "رصيد المتجر",
};

interface RequestReturnModalProps {
  orderRef: string;
  customerName: string;
  customerPhone: string;
  orderItems: { nameAr: string }[];
  onClose: () => void;
  onSubmitted: () => void;
}

function RequestReturnModal({
  orderRef,
  customerName,
  customerPhone,
  orderItems,
  onClose,
  onSubmitted,
}: RequestReturnModalProps) {
  const [productName, setProductName] = useState(orderItems[0]?.nameAr ?? "");
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productName.trim() || !reason.trim()) return;
    setSaving(true);
    const { error } = await supabase.from("returns").insert({
      order_id: orderRef,
      customer_name: customerName,
      customer_phone: customerPhone || null,
      product_name: productName.trim(),
      quantity,
      reason: reason.trim(),
      status: "pending",
    });
    setSaving(false);
    if (error) return;
    setSubmitted(true);
    setTimeout(() => {
      onSubmitted();
      onClose();
    }, 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md bg-[#141414] border border-white/8 rounded-2xl overflow-hidden"
        dir="rtl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-400/10 rounded-lg">
              <i className="ri-arrow-go-back-line text-amber-400 text-sm"></i>
            </div>
            <div>
              <h3 className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>
                طلب إرجاع
              </h3>
              <p className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
                طلب #{orderRef}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-gray-500 hover:text-white transition-all cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center justify-center py-12 px-6 gap-4">
            <div className="w-16 h-16 flex items-center justify-center bg-emerald-400/10 border border-emerald-400/20 rounded-2xl">
              <i className="ri-checkbox-circle-line text-emerald-400 text-3xl"></i>
            </div>
            <div className="text-center">
              <p className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
                تم إرسال طلب الإرجاع!
              </p>
              <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                سيتم مراجعة طلبك والرد عليك قريباً
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
            {/* Product selector */}
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                المنتج المُراد إرجاعه <span className="text-red-400">*</span>
              </label>
              {orderItems.length > 1 ? (
                <select
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-sm text-white outline-none focus:border-amber-400/40 transition-colors cursor-pointer"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  {orderItems.map((item, i) => (
                    <option key={i} value={item.nameAr}>{item.nameAr}</option>
                  ))}
                </select>
              ) : (
                <div className="bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-2.5 text-sm text-gray-300" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {productName}
                </div>
              )}
            </div>

            {/* Quantity */}
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                الكمية
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] border border-white/8 rounded-lg text-gray-400 hover:text-white hover:border-white/20 transition-all cursor-pointer"
                >
                  <i className="ri-subtract-line text-sm"></i>
                </button>
                <span className="text-white font-bold text-base w-8 text-center" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {quantity}
                </span>
                <button
                  type="button"
                  onClick={() => setQuantity(q => q + 1)}
                  className="w-9 h-9 flex items-center justify-center bg-[#1a1a1a] border border-white/8 rounded-lg text-gray-400 hover:text-white hover:border-white/20 transition-all cursor-pointer"
                >
                  <i className="ri-add-line text-sm"></i>
                </button>
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="block text-xs font-bold text-gray-400 mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                سبب الإرجاع <span className="text-red-400">*</span>
              </label>
              {/* Quick reasons */}
              <div className="flex flex-wrap gap-2 mb-2">
                {["منتج معيب", "مقاس خاطئ", "غير مطابق للوصف", "تغيير رأي"].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      reason === r
                        ? "bg-amber-400/15 border-amber-400/30 text-amber-400"
                        : "bg-white/5 border-white/8 text-gray-500 hover:text-gray-300 hover:border-white/15"
                    }`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="اشرح سبب الإرجاع بالتفصيل..."
                rows={3}
                maxLength={500}
                className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none focus:border-amber-400/40 transition-colors resize-none"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              />
              <p className="text-[10px] text-gray-600 mt-1 text-left">{reason.length}/500</p>
            </div>

            {/* Info note */}
            <div className="flex items-start gap-2 bg-white/3 border border-white/5 rounded-xl px-4 py-3">
              <i className="ri-information-line text-gray-600 text-sm mt-0.5 flex-shrink-0"></i>
              <p className="text-gray-600 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                سيتم مراجعة طلبك خلال 1-3 أيام عمل. سنتواصل معك على رقم الجوال المسجل.
              </p>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-white/5 hover:bg-white/8 border border-white/8 text-gray-400 font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={saving || !reason.trim()}
                className="flex-1 flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-black font-bold py-3 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin"></div>جاري الإرسال...</>
                ) : (
                  <><i className="ri-send-plane-line text-sm"></i>إرسال الطلب</>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

interface OrderReturnsProps {
  orderRef: string;
  customerName: string;
  customerPhone: string;
  orderItems: { nameAr: string }[];
  orderStatus: string;
}

export default function OrderReturns({
  orderRef,
  customerName,
  customerPhone,
  orderItems,
  orderStatus,
}: OrderReturnsProps) {
  const [returns, setReturns] = useState<ReturnItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("returns")
      .select("*")
      .eq("order_id", orderRef)
      .order("created_at", { ascending: false });
    setLoading(false);
    setReturns(data ?? []);
  }, [orderRef]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const canRequestReturn = orderStatus === "delivered";
  const hasPendingReturn = returns.some((r) => r.status === "pending");

  if (loading) return null;

  return (
    <>
      <div className="bg-[#141414] border border-white/5 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <h2
            className="text-white font-bold text-sm flex items-center gap-2"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          >
            <div className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-lg">
              <i className="ri-arrow-go-back-line text-amber-400 text-sm"></i>
            </div>
            المرتجعات
            {returns.length > 0 && (
              <span className="bg-white/5 text-gray-400 text-xs px-2.5 py-0.5 rounded-full font-normal">
                {returns.length}
              </span>
            )}
          </h2>

          {canRequestReturn && !hasPendingReturn && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1.5 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/20 text-amber-400 font-bold text-xs px-4 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <i className="ri-add-line text-sm"></i>
              طلب إرجاع
            </button>
          )}

          {hasPendingReturn && (
            <span
              className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/20 text-amber-400 text-xs font-bold px-3 py-1.5 rounded-full"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse inline-block"></span>
              طلب قيد المراجعة
            </span>
          )}
        </div>

        <div className="p-6">
          {returns.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-white/3 border border-white/5 rounded-2xl">
                <i className="ri-arrow-go-back-line text-gray-700 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-500 text-sm font-medium" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  لا توجد مرتجعات لهذا الطلب
                </p>
                {canRequestReturn ? (
                  <p className="text-gray-700 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    يمكنك طلب إرجاع أي منتج من الطلب
                  </p>
                ) : (
                  <p className="text-gray-700 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    الإرجاع متاح فقط بعد استلام الطلب
                  </p>
                )}
              </div>
              {canRequestReturn && (
                <button
                  onClick={() => setShowModal(true)}
                  className="flex items-center gap-2 bg-amber-400/10 hover:bg-amber-400/15 border border-amber-400/20 text-amber-400 font-bold text-xs px-5 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap mt-1"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-arrow-go-back-line text-sm"></i>
                  طلب إرجاع الآن
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {returns.map((ret) => {
                const cfg = STATUS_CONFIG[ret.status];
                const isExpanded = expanded === ret.id;
                const date = new Date(ret.created_at).toLocaleDateString("ar-SA", {
                  year: "numeric", month: "short", day: "numeric",
                });

                return (
                  <div
                    key={ret.id}
                    className="bg-[#1a1a1a] border border-white/5 rounded-xl overflow-hidden transition-all"
                  >
                    {/* Row */}
                    <button
                      onClick={() => setExpanded(isExpanded ? null : ret.id)}
                      className="w-full flex items-center gap-4 px-4 py-3.5 text-right cursor-pointer hover:bg-white/3 transition-colors"
                    >
                      {/* Status icon */}
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${cfg.bg} border ${cfg.border}`}>
                        <i className={`${cfg.icon} ${cfg.color} text-base`}></i>
                      </div>

                      <div className="flex-1 min-w-0 text-right">
                        <p className="text-white text-sm font-bold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          {ret.product_name}
                        </p>
                        <p className="text-gray-600 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                          الكمية: {ret.quantity} · {date}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}
                          style={{ fontFamily: "'Cairo', sans-serif" }}>
                          <i className={`${cfg.icon} text-xs`}></i>
                          {cfg.label}
                        </span>
                        <i className={`${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-600 text-base`}></i>
                      </div>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-white/5 pt-4 flex flex-col gap-3">
                        {/* Reason */}
                        <div>
                          <p className="text-gray-600 text-xs font-bold mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                            سبب الإرجاع
                          </p>
                          <div className="bg-[#141414] border border-white/5 rounded-xl px-4 py-3">
                            <p className="text-gray-300 text-sm leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              {ret.reason}
                            </p>
                          </div>
                        </div>

                        {/* Refund info */}
                        {(ret.refund_amount || ret.refund_method) && (
                          <div className="flex items-center gap-4 bg-emerald-400/5 border border-emerald-400/15 rounded-xl px-4 py-3">
                            <i className="ri-refund-2-line text-emerald-400 text-lg flex-shrink-0"></i>
                            <div>
                              <p className="text-emerald-400 text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
                                {ret.refund_amount
                                  ? `${ret.refund_amount.toLocaleString("ar-SA")} ر.س`
                                  : ""}
                                {ret.refund_amount && ret.refund_method ? " · " : ""}
                                {ret.refund_method ? REFUND_METHOD_LABELS[ret.refund_method] ?? ret.refund_method : ""}
                              </p>
                              <p className="text-emerald-400/60 text-xs mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                                مبلغ الاسترداد
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Admin notes */}
                        {ret.admin_notes && (
                          <div>
                            <p className="text-gray-600 text-xs font-bold mb-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              ملاحظة من الفريق
                            </p>
                            <div className="flex items-start gap-2 bg-amber-400/5 border border-amber-400/10 rounded-xl px-4 py-3">
                              <i className="ri-customer-service-2-line text-amber-400/60 text-sm mt-0.5 flex-shrink-0"></i>
                              <p className="text-amber-400/80 text-sm leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                                {ret.admin_notes}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Rejected notice */}
                        {ret.status === "rejected" && (
                          <div className="flex items-start gap-2 bg-red-400/5 border border-red-400/10 rounded-xl px-4 py-3">
                            <i className="ri-information-line text-red-400/60 text-sm mt-0.5 flex-shrink-0"></i>
                            <p className="text-red-400/70 text-xs leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                              تم رفض طلب الإرجاع. للاستفسار تواصل معنا عبر واتساب.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add another return */}
              {canRequestReturn && !hasPendingReturn && (
                <button
                  onClick={() => setShowModal(true)}
                  className="w-full flex items-center justify-center gap-2 border border-dashed border-white/10 hover:border-amber-400/30 text-gray-600 hover:text-amber-400 text-xs py-3 rounded-xl transition-all cursor-pointer"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-add-line text-sm"></i>
                  طلب إرجاع منتج آخر
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <RequestReturnModal
          orderRef={orderRef}
          customerName={customerName}
          customerPhone={customerPhone}
          orderItems={orderItems}
          onClose={() => setShowModal(false)}
          onSubmitted={fetchReturns}
        />
      )}
    </>
  );
}
